// Timeline for the simulated MCP conversation on the login page, in the style of
// the MCP launch post: a question, tool calls, typing dots, a streamed answer,
// staged blocks (findings, action preview), pauses and the confirmation tap.

export const SHOWCASE_WORD_DELAY_MS = 42;
export const SHOWCASE_ITEM_DELAY_MS = 160;
export const SHOWCASE_HOLD_MS = 5000;

const START_MS = 400;
const STEP_MS = {
  question: 900,
  tool: 520,
  confirm: 700,
};
const TYPING_LEAD_MS = 150;
const TYPING_MS = 1300;
const STREAM_TAIL_MS = 450;
const BLOCK_TAIL_MS = 550;

const toCount = (value, minimum) => (Number.isFinite(value) ? Math.max(minimum, Math.floor(value)) : minimum);

export const countWords = (text) => String(text || "").trim().split(/\s+/).filter(Boolean).length;

/**
 * Turns a conversation script into chronological visibility events.
 * Step kinds: question, tool, typing (shown then hidden), stream ({ words }),
 * block ({ items }), confirm and pause ({ ms }, no event).
 * Returns { events: [{ at, id, hide? }], duration } in milliseconds.
 */
export function buildShowcaseTimeline(steps = []) {
  const events = [];
  let t = START_MS;

  steps.forEach((step) => {
    const { id, kind } = step || {};
    if (kind === "pause") {
      t += toCount(step.ms, 0);
      return;
    }
    if (!id) return;

    if (kind === "typing") {
      t += TYPING_LEAD_MS;
      events.push({ at: t, id });
      t += TYPING_MS;
      events.push({ at: t, id, hide: true });
    } else if (kind === "stream") {
      events.push({ at: t, id });
      t += toCount(step.words, 0) * SHOWCASE_WORD_DELAY_MS + STREAM_TAIL_MS;
    } else if (kind === "block") {
      events.push({ at: t, id });
      t += toCount(step.items, 1) * SHOWCASE_ITEM_DELAY_MS + BLOCK_TAIL_MS;
    } else if (STEP_MS[kind]) {
      events.push({ at: t, id });
      t += STEP_MS[kind];
    }
  });

  return { events, duration: t };
}

/** Ids that are visible once the whole conversation has played (typing dots excluded). */
export function finalShowcaseState(events) {
  const visible = new Set();
  events.forEach(({ id, hide }) => (hide ? visible.delete(id) : visible.add(id)));
  return visible;
}

const ROUND_PAUSE_MS = 1400;
const CONFIRM_PAUSE_MS = 900;

/**
 * Builds the playback script of one narrative. Each round is a user message
 * followed by the assistant reply: tool calls, then an optional streamed answer,
 * finding cards, and an action preview that is confirmed before the closing reply.
 */
export function buildNarrativeSteps(rounds = [], toolsByRound = []) {
  const steps = [];
  rounds.forEach((round, index) => {
    if (!round) return;
    const id = (name) => `r${index}-${name}`;
    const tools = Array.isArray(toolsByRound[index]) ? toolsByRound[index] : [];
    if (index > 0) steps.push({ kind: "pause", ms: ROUND_PAUSE_MS });

    steps.push({ id: id("question"), kind: "question" });
    tools.forEach((_, toolIndex) => steps.push({ id: id(`tool-${toolIndex}`), kind: "tool" }));
    if (round.answer) {
      steps.push({ id: id("typing"), kind: "typing" });
      steps.push({ id: id("answer"), kind: "stream", words: countWords(round.answer) });
    }
    if (Array.isArray(round.findings) && round.findings.length) {
      steps.push({ id: id("findings"), kind: "block", items: round.findings.length });
    }
    if (round.preview) {
      const items = Array.isArray(round.preview.items) ? round.preview.items.length : 0;
      steps.push({ id: id("preview"), kind: "block", items: items + 2 });
      steps.push({ kind: "pause", ms: CONFIRM_PAUSE_MS });
      steps.push({ id: id("confirm"), kind: "confirm" });
    }
    if (round.done) {
      steps.push({ id: id("done-typing"), kind: "typing" });
      steps.push({ id: id("done"), kind: "stream", words: countWords(`${round.doneLead || ""} ${round.done}`) });
    }
  });
  return steps;
}

/**
 * Picks a narrative at random for each page load, avoiding the one shown last
 * so a refresh always tells a different story.
 */
export function pickNarrativeIndex(count, previousIndex = null, random = Math.random) {
  const total = toCount(count, 0);
  if (total <= 1) return 0;
  const hasPrevious = Number.isInteger(previousIndex) && previousIndex >= 0 && previousIndex < total;
  const choices = hasPrevious ? total - 1 : total;
  const value = Number(random());
  const draw = Math.min(choices - 1, Math.floor((Number.isFinite(value) ? Math.abs(value) % 1 : 0) * choices));
  return hasPrevious && draw >= previousIndex ? draw + 1 : draw;
}
